import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PackageSearch, RefreshCw, Search } from 'lucide-react';
import { EmptyState, MetricCard, PageHeader } from '../components/UI';
import ShopOrderManagement from '../components/ShopOrderManagement';
import { api } from '../lib/api';

export default function OrdersWorkspace(){
  const[params,setParams]=useSearchParams();
  const[data,setData]=useState({orders:[],metrics:{}});
  const[loading,setLoading]=useState(true);
  const[error,setError]=useState('');
  const[q,setQ]=useState(()=>params.get('q')||'');
  const[status,setStatus]=useState(()=>params.get('status')||'');

  const load=useCallback(async()=>{
    setLoading(true);setError('');
    try{
      const params=new URLSearchParams();
      if(q.trim())params.set('q',q.trim());
      if(status)params.set('status',status);
      const result=await api('/api/shop-orders'+(params.toString()?'?'+params.toString():''));
      setData(result);
    }catch(e){setError(e.message);}
    finally{setLoading(false);}
  },[q,status]);

  useEffect(()=>{const timer=setTimeout(()=>{void load();},q?220:0);return()=>clearTimeout(timer);},[load,q,status]);
  useEffect(()=>{const next={};if(q.trim())next.q=q.trim();if(status)next.status=status;setParams(next,{replace:true});},[q,status,setParams]);

  if(loading&&!data.orders?.length)return <EmptyState title="Loading shop orders" body="Reading live Sousa Murray Shop, Stripe and fulfilment-provider records…"/>;
  if(error&&!data.orders?.length)return <EmptyState title="Shop orders unavailable" body={error}/>;

  const metrics=data.metrics||{};
  return <>
    <PageHeader
      eyebrow="Commercial"
      title="Orders"
      description="Operational control centre for Sousa Murray Shop quotes, paid orders, Stripe documents and live Prodigi/CJ fulfilment. Quotes remain separate from orders until payment completes."
      actions={<button className="button secondary govuk-button govuk-button--secondary" onClick={()=>void load()}><RefreshCw size={15}/>Refresh</button>}
    />
    {error&&<div className="operation-notice error">{error}</div>}
    <div className="metrics-grid">
      <MetricCard label="Commerce records" value={metrics.total??data.orders.length} detail="Quotes + orders"/>
      <MetricCard label="Open quotes" value={metrics.quotes??0} detail="Not yet paid or submitted" accent="amber"/>
      <MetricCard label="Awaiting fulfilment" value={metrics.awaitingFulfilment??0} detail="Ready or retry required" accent="amber"/>
      <MetricCard label="In fulfilment" value={metrics.inFulfilment??0} detail="Live with fulfilment providers" accent="green"/>
      <MetricCard label="Fulfilled" value={metrics.fulfilled??0} detail="Completed orders"/>
      <MetricCard label="Cancelled" value={metrics.cancelled??0} detail="Provider/local cancellation"/>
      <MetricCard label="Provider issues" value={metrics.providerIssues??0} detail="Requires staff review" accent="amber"/>
      <MetricCard label="Test payments" value={metrics.testPayments??0} detail="Stripe sandbox · never fulfilled" accent="amber"/>
    </div>
    <div className="data-toolbar">
      <div className="toolbar-search"><Search size={17}/><input className="govuk-input" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search order, customer, supplier ID or email"/></div>
      <select className="govuk-select" value={status} onChange={e=>setStatus(e.target.value)} aria-label="Filter orders by status">
        <option value="">All statuses</option>
        <option value="quote">Open quotes</option>
        <option value="test_paid">Stripe test payments</option>
        <option value="paid_awaiting_fulfilment">Awaiting fulfilment</option>
        <option value="fulfilment_retry">Fulfilment retry</option>
        <option value="in_fulfilment">In fulfilment</option>
        <option value="fulfilled">Fulfilled</option>
        <option value="cancelled">Cancelled</option>
      </select>
      <span className="source-chip"><PackageSearch size={14}/> Stripe + Prodigi operational control</span>
    </div>
    <ShopOrderManagement orders={data.orders||[]} onChanged={load}/>
  </>;
}
